import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  googleProvider,
  setAuthTenant,
  sendEmailVerification,
} from './firebase';

// Create Auth Context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantType, setTenantType] = useState(() => {
    // Initialize tenant type from localStorage
    return localStorage.getItem('tenantType');
  });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setLoading(false);
      
      // If user is null (logged out), clear tenant type
      if (!user) {
        setTenantType(null);
        localStorage.removeItem('tenantType');
      } else {
        // If user is logged in and we have a stored tenant type, restore the tenant
        const storedTenantType = localStorage.getItem('tenantType');
        if (storedTenantType && !tenantType) {
          setTenant(storedTenantType);
        }
      }
    });

    return unsubscribe;
  }, [tenantType]);

  const setTenant = (tenantType) => {
    let tenantId;
    setTenantType(tenantType); // Store the tenant type
    localStorage.setItem('tenantType', tenantType); // Persist tenant type
    switch (tenantType) {
      case 'agency':
        tenantId = process.env.REACT_APP_TENANT_AGENCY;
        break;
      case 'employee':
        tenantId = process.env.REACT_APP_TENANT_EMPLOYEE;
        break;
      case 'employer':
        tenantId = process.env.REACT_APP_TENANT_EMPLOYER;
        break;
      default:
        tenantId = process.env.REACT_APP_TENANT_MASTER;
    }
    setAuthTenant(tenantId);
  };

  const login = async (email, password, tenantType) => {
    setTenant(tenantType);
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Always navigate to dashboard first - middleware will handle email verification
    navigate(tenantType === 'agency' ? '/agency' : '/employer');
  };

  const loginWithGoogle = async (tenantType) => {
    setTenant(tenantType);
    const userCredential = await signInWithPopup(auth, googleProvider);
    
    // Google accounts are typically pre-verified, but send verification if needed
    if (!userCredential.user.emailVerified) {
      await sendEmailVerification(userCredential.user);
    }
    
    // Always navigate to dashboard - middleware will handle email verification
    navigate(tenantType === 'agency' ? '/agency' : '/employer');
  };

  const signup = async (email, password, tenantType) => {
    setTenant(tenantType);
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Send email verification immediately after signup
    await sendEmailVerification(userCredential.user);
    
    // User is automatically signed in after createUserWithEmailAndPassword
    // The middleware will handle the email verification screen
  };

  const logout = async () => {
    // Clear tenant type from localStorage before signing out
    setTenantType(null);
    localStorage.removeItem('tenantType');
    await signOut(auth);
    navigate('/login');
  };

  const value = {
    currentUser,
    tenantType, // Provide tenant type in the context
    login,
    loginWithGoogle,
    signup,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};

